import type { ApiError } from '../api/client';

export function ErrorBox({ error }: { error: ApiError | null }) {
  if (!error) return null;
  return (
    <div className="alert alert-danger" role="alert">
      {error.message}
      {error.errors.length > 0 && (
        <ul className="small" style={{ margin: '6px 0 0' }}>
          {error.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const MONTHS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

/** Date partielle affichée telle quelle : jamais de jour ou de mois inventé. */
export function formatPartialDate(d: {
  year: number | null;
  month: number | null;
  day: number | null;
}): string {
  if (d.year === null) return 'inconnue';
  if (d.month === null) return `${d.year} (jour et mois inconnus)`;
  if (d.day === null) return `${MONTHS[d.month - 1]} ${d.year} (jour inconnu)`;
  return `${d.day} ${MONTHS[d.month - 1]} ${d.year}`;
}

export function formatDay(iso: string | null): string {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(iso));
}

export const QUALITY_LABELS: Record<string, string> = {
  verifiee: 'vérifiée',
  a_verifier: 'à vérifier',
  illisible: 'illisible',
  contradictoire: 'contradictoire',
  absente: 'absente',
};

export const INSCRIPTION_STATUS: Record<string, { label: string; tone: string }> = {
  brouillon: { label: 'Brouillon', tone: 'muted' },
  soumise: { label: 'À valider', tone: 'info' },
  a_corriger: { label: 'À corriger', tone: 'warn' },
  validee: { label: 'Validée', tone: 'ok' },
  rejetee: { label: 'Rejetée', tone: 'danger' },
  effacee: { label: 'Effacée', tone: 'muted' },
};

export const OUTCOME_LABELS: Record<string, { label: string; tone: string; help: string }> = {
  confirmee: {
    label: 'Correspondance confirmée',
    tone: 'ok',
    help: 'Confirmée par une vérification humaine.',
  },
  candidats_a_examiner: {
    label: 'Candidat à examiner',
    tone: 'info',
    help: 'Une personne ressemble fortement : vérifiez les pièces avant de la retenir.',
  },
  conflit: {
    label: 'Conflit - plusieurs personnes proches',
    tone: 'danger',
    help: 'Plusieurs personnes correspondent : ne rattachez rien sans résolution par un responsable des rapprochements.',
  },
  non_concluante: {
    label: 'Recherche non concluante',
    tone: 'warn',
    help: 'Des ressemblances partielles seulement : complétez les critères ou vérifiez les sources.',
  },
  absence_dans_perimetre: {
    label: 'Aucune personne dans le périmètre interrogé',
    tone: 'muted',
    help: 'Le registre numérique ne contient personne de proche. Cela ne prouve pas l’absence d’inscription.',
  },
};

export function Badge({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}
